import {useEffect} from 'react';

import {
  markCurrentMessageApprovedForSend,
  readComposeAttachments,
  readComposeMessageMetadata,
  sendCurrentMessage,
  setOutlookEmailSyncHeader,
} from './outlookComposeItem';
import * as Roaming from './roamingSettings';
import type {RoamingSettingsState} from './RoamingSettingsContext';
import {
  createDialogSessionToken,
  type DialogMessagePayload,
  parseDialogMessage,
  type RoamingSettingsUpdate,
  SEND_DIALOG_ATTACHMENT_SYNC_INTERVAL_MS,
  sendReviewDialogUrl,
  stringifyDialogMessage,
} from './sendDialogProtocol';
import {usePodRequest} from './usePodRequest';

type Options = {
  enabled: boolean;
  onClose: () => void;
  onOpenFailed: () => void;
};

const DIALOG_ALREADY_OPEN_ERROR_CODE = 12007;
const DIALOG_ALREADY_OPEN_RETRY_LIMIT = 4;
const DIALOG_ALREADY_OPEN_RETRY_DELAY_MS = 500;

const readRoamingSettingsState = (): RoamingSettingsState => ({
  emailSyncOnSend: Roaming.getEmailSyncOnSend() ?? true,
  podUrl: Roaming.getPodUrl(),
  refreshToken: Roaming.getRefreshToken(),
  jwt: Roaming.getJwt(),
});

const applyRoamingSettingsUpdate = async (
  updates: RoamingSettingsUpdate
): Promise<RoamingSettingsState> => {
  if (updates.emailSyncOnSend !== undefined) {
    await Roaming.setEmailSyncOnSend(updates.emailSyncOnSend);
  }
  if (updates.podUrl !== undefined) {
    await Roaming.setPodUrl(updates.podUrl);
  }
  if (updates.refreshToken !== undefined) {
    await Roaming.setRefreshToken(updates.refreshToken);
  }
  if (updates.clearJwtAndRefreshTokens) {
    await Roaming.clearJwtAndRefreshTokens();
  }
  return readRoamingSettingsState();
};

export const useSendReviewDialog = ({
  enabled,
  onClose,
  onOpenFailed,
}: Options): void => {
  const {ensureAccessToken} = usePodRequest();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (typeof Office === 'undefined' || !Office.context?.ui) {
      onOpenFailed();
      return;
    }

    const dialogSessionToken = createDialogSessionToken();
    const dialogUrl = sendReviewDialogUrl(dialogSessionToken);
    let dialog: Office.Dialog | null = null;
    let attachmentSyncInterval: number | null = null;
    let cancelled = false;
    let closed = false;

    const closeOfficeDialog = (dialogToClose = dialog) => {
      if (dialog === dialogToClose) {
        dialog = null;
      }
      if (dialogToClose) {
        try {
          dialogToClose.close();
        } catch {
          // Dialog may already be closed.
        }
      }
    };

    const messageChild = (payload: DialogMessagePayload) => {
      dialog?.messageChild(stringifyDialogMessage(payload));
    };

    const stopAttachmentSync = () => {
      if (attachmentSyncInterval != null) {
        window.clearInterval(attachmentSyncInterval);
        attachmentSyncInterval = null;
      }
    };

    const closeDialog = () => {
      if (closed) {
        return;
      }
      closed = true;
      stopAttachmentSync();
      try {
        closeOfficeDialog();
      } finally {
        onClose();
      }
    };

    const syncAttachments = () => {
      void readComposeAttachments().then((attachments) => {
        messageChild({type: 'attachments', attachments});
      });
    };

    const startAttachmentSync = () => {
      if (attachmentSyncInterval != null) {
        return;
      }
      syncAttachments();
      attachmentSyncInterval = window.setInterval(
        syncAttachments,
        SEND_DIALOG_ATTACHMENT_SYNC_INTERVAL_MS
      );
    };

    const sendRoamingSettings = () => {
      void ensureAccessToken()
        .catch(() => undefined)
        .then((accessToken) => {
          messageChild({
            type: 'roamingSettings',
            settings: {...readRoamingSettingsState(), jwt: accessToken},
          });
        });
    };

    const sendComposeMetadata = () => {
      void readComposeMessageMetadata().then((metadata) => {
        messageChild({type: 'composeMessageMetadata', metadata});
      });
    };

    const isTrustedDialogMessage = (
      arg: {origin?: string},
      message: DialogMessagePayload
    ) => {
      if (arg.origin && arg.origin !== window.location.origin) {
        return false;
      }
      return message.parentDialogToken === dialogSessionToken;
    };

    let openAttemptsLeft = DIALOG_ALREADY_OPEN_RETRY_LIMIT;
    let retryTimeout: number | null = null;

    const openDialog = () => {
      Office.context.ui.displayDialogAsync(
        dialogUrl,
        {height: 75, width: 55, displayInIframe: false},
        onDialogOpened
      );
    };

    const onDialogOpened = (asyncResult: Office.AsyncResult<Office.Dialog>) => {
      if (cancelled) {
        closeOfficeDialog(asyncResult.value ?? null);
        return;
      }

      if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
        if (
          asyncResult.error?.code === DIALOG_ALREADY_OPEN_ERROR_CODE &&
          openAttemptsLeft > 0
        ) {
          openAttemptsLeft -= 1;
          retryTimeout = window.setTimeout(
            openDialog,
            DIALOG_ALREADY_OPEN_RETRY_DELAY_MS
          );
          return;
        }
        onOpenFailed();
        return;
      }

      dialog = asyncResult.value;

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        if (!('message' in arg)) {
          return;
        }

        const message = parseDialogMessage(arg.message);
        if (!message) {
          return;
        }

        if (!isTrustedDialogMessage(arg, message)) {
          closeDialog();
          return;
        }

        if (message.type === 'ready') {
          startAttachmentSync();
          sendRoamingSettings();
          sendComposeMetadata();
          return;
        }

        if (message.type === 'getComposeMessageMetadata') {
          sendComposeMetadata();
          return;
        }

        if (message.type === 'getRoamingSettings') {
          sendRoamingSettings();
          return;
        }

        if (message.type === 'updateRoamingSettings') {
          void applyRoamingSettingsUpdate(
            (message.updates ?? {}) as RoamingSettingsUpdate
          )
            .then((settings) => {
              messageChild({
                type: 'roamingSettingsUpdated',
                ok: true,
                settings,
              });
            })
            .catch((error: unknown) => {
              messageChild({
                type: 'roamingSettingsUpdated',
                ok: false,
                error:
                  error instanceof Error
                    ? error.message
                    : 'Failed to update roaming settings',
              });
            });
          return;
        }

        if (message.type === 'setEmailSyncHeader') {
          void setOutlookEmailSyncHeader(String(message.headerId ?? ''))
            .then(() => {
              messageChild({type: 'emailSyncHeaderSet', ok: true});
            })
            .catch((error: unknown) => {
              messageChild({
                type: 'emailSyncHeaderSet',
                ok: false,
                error:
                  error instanceof Error
                    ? error.message
                    : 'Failed to set email sync header',
              });
            });
          return;
        }

        if (message.status === 'success') {
          stopAttachmentSync();
          void markCurrentMessageApprovedForSend()
            .then(() => {
              const sendPromise = sendCurrentMessage();
              closeDialog();
              return sendPromise;
            })
            .catch(closeDialog);
          return;
        }

        closeDialog();
      });

      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
        closeDialog();
      });
    };

    openDialog();

    return () => {
      cancelled = true;
      if (retryTimeout != null) {
        window.clearTimeout(retryTimeout);
      }
      stopAttachmentSync();
      closeOfficeDialog();
    };
  }, [enabled, ensureAccessToken, onClose, onOpenFailed]);
};
